import { useState, useEffect, useRef } from "react"
import { Tabs, Radio, Skeleton } from "antd"
import { useMutation } from "@tanstack/react-query"
import { queryClient } from "index"
import { ewaAxiosInstance } from "utils/request"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import {
  IShiftSetupRes,
  ITimeSheetRes,
  IClockedInStatusRes,
} from "../interface/shiftData"
import {
  calculateDistanceInMeters,
  getClockStatus,
  getCurrentTime,
} from "../utils"
import { ClockingCard } from "./ClockingCard"
import CustomToast from "./CustomToast"
import { SelectShiftPeriodContent } from "./SelectShiftPeriodContent"

type GeolocationStatus = "idle" | "checking" | "resolved" | "failed"

const EmployeeClockInOut = ({
  shiftSetupData,
  timesheetData,
  isWorkplace,
  timesheetQueryKey,
  clockinStatus,
  isLoading,
  groupBy,
}: {
  timesheetData: ITimeSheetRes | undefined
  timesheetQueryKey: string
  isWorkplace: boolean
  groupBy: "day" | "week" | "month"
  clockinStatus: IClockedInStatusRes | undefined
  shiftSetupData: IShiftSetupRes | undefined
  isLoading: boolean
}) => {
  const [activeTab, setActiveTab] = useState("1")
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [employeeLatitude, setEmployeeLatitude] = useState<number | null>(null)
  const [employeeLongitude, setEmployeeLongitude] = useState<number | null>(
    null
  )
  const [geolocationStatus, setGeolocationStatus] =
    useState<GeolocationStatus>("idle")
  const [currentTime, setCurrentTime] = useState(getCurrentTime())
  const { toast } = useAppNotificationStore()

  const shiftPeriods = shiftSetupData?.data?.shiftPeriods
  const dutyLocation = shiftSetupData?.data?.location
  const dutyLocationId = shiftSetupData?.data?.location?.id
  const shiftPlans = shiftSetupData?.data?.shiftPlans

  const selectedPeriod = shiftPeriods?.find((p) => p.id === selectedPeriodId)

  const assignedShiftPlanId = selectedPeriod?.shiftPlanId

  const shiftPlan = assignedShiftPlanId
    ? shiftPlans?.find((plan) => plan.id === assignedShiftPlanId)
    : undefined

  const isOvertimeAllowed = shiftPlan?.useOvertimeRecord ?? false

  const { mutate: mutateClockIn, isLoading: isLoadingClockIn } = useMutation({
    mutationFn: async (body: {
      latitude: number | null
      longitude: number | null
      method: "web-app"
      reason: string
      dutyLocationId?: number | null
    }) => {
      return (
        await ewaAxiosInstance.post(
          `/v1/time-attendance/shift-plans/${shiftPlan?.id}/clock-in`,
          body
        )
      ).data
    },
    onSuccess: (data) => {
      if (data.success) {
        CustomToast("success", "Clock-in successful")
        queryClient.invalidateQueries({ queryKey: [timesheetQueryKey] })
        queryClient.invalidateQueries({
          queryKey: ["/v1/time-attendance/shift-plans/clock-in/check-status"],
        })
      }
    },
    onError: onQueryError,
  })

  const { mutate: mutateClockout, isLoading: isLoadingClockout } = useMutation({
    mutationFn: async (body: {
      latitude: number | null
      longitude: number | null
      overtimeSubmission: boolean
    }) => {
      return (
        await ewaAxiosInstance.post(
          `/v1/time-attendance/timesheets/${timesheetId}/clock-out`,
          body
        )
      ).data
    },
    onSuccess: (data) => {
      if (data.success) {
        CustomToast("success", "Clock-out successful")
        queryClient.invalidateQueries({ queryKey: [timesheetQueryKey] })
        queryClient.invalidateQueries({
          queryKey: ["/v1/time-attendance/shift-plans/clock-in/check-status"],
        })
      }
    },
    onError: onQueryError,
  })

  const geofenceEnabled = shiftPlan?.allowGeoFencing
  useEffect(() => {
    if (!geofenceEnabled) {
      setGeolocationStatus("idle")
      setEmployeeLatitude(null)
      setEmployeeLongitude(null)
      return
    }

    if (!navigator.geolocation) {
      setGeolocationStatus("failed")
      return
    }

    setGeolocationStatus("checking")
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setEmployeeLatitude(position.coords.latitude)
        setEmployeeLongitude(position.coords.longitude)
        setGeolocationStatus("resolved")
      },
      () => {
        setEmployeeLatitude(null)
        setEmployeeLongitude(null)
        setGeolocationStatus("failed")
      },
      {
        enableHighAccuracy: true,
        maximumAge: 60000,
        timeout: 10000,
      }
    )
  }, [geofenceEnabled])

  const isWithinGeofence = (() => {
    if (
      !geofenceEnabled ||
      !dutyLocation ||
      employeeLatitude === null ||
      employeeLongitude === null
    )
      return null

    const distance = calculateDistanceInMeters(
      employeeLatitude,
      employeeLongitude,
      dutyLocation.latitude,
      dutyLocation.longitude
    )

    return distance <= shiftPlan.geofencingOffsetMeters
  })()

  const geofenceDisplay = !geofenceEnabled
    ? null
    : geolocationStatus === "checking"
    ? null
    : isWithinGeofence
    ? "Yes"
    : isWithinGeofence === false
    ? "No"
    : "Unavailable"

  const geofenceError =
    geofenceEnabled && geolocationStatus === "failed"
      ? "Location access is blocked or unavailable. Enable location permission in your browser site settings, then refresh this page."
      : null

  const clockInStatus = selectedPeriod
    ? getClockStatus(selectedPeriod.startTime, "clock-in")
    : null

  const clockOutStatus = selectedPeriod
    ? getClockStatus(selectedPeriod.endTime, "clock-out")
    : null

  const alreadyClockedIn = clockinStatus?.data?.clockedIn
  const alreadyClockedOut = clockinStatus?.data?.details?.clockOut
  const timesheetId = clockinStatus?.data?.details?.id

  useEffect(() => {
    if (alreadyClockedIn) {
      setActiveTab("2")
    }
  }, [alreadyClockedIn])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTime())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="block lg:flex items-stretch gap-3 mb-2">
      <div className="mt-3 bg-white p-4 w-full md:flex-1">
        <h5 className="text-base text-padeBlack mb-3 font-semibold shrink-0">
          Clock-in / Clock out
        </h5>
        <div>
          <Tabs
            activeKey={activeTab}
            items={[
              {
                key: "1",
                label: "I'm clocking in",
                children: (
                  <>
                    <p className="text-sm text-slate-900 font-bold mb-2">
                      Select shift period you're clocking in for
                    </p>
                    <Radio.Group
                      style={{ width: "100%" }}
                      value={selectedPeriodId}
                      onChange={(e) => setSelectedPeriodId(e.target.value)}
                    >
                      <div className="grid [grid-template-columns:repeat(auto-fit,minmax(250px,1fr))] justify-between items-center gap-1">
                        {isLoading ? (
                          <Skeleton.Button
                            active
                            block
                            style={{ height: 60, borderRadius: 6 }}
                          />
                        ) : (
                          shiftPeriods?.map((period) => (
                            <ClockingCard
                              key={period.id}
                              value={period.id}
                              title={period.title}
                              clockIn={period.startTime}
                              clockOut={period.endTime}
                              onClick={() => setSelectedPeriodId(period.id)}
                            />
                          ))
                        )}
                      </div>
                    </Radio.Group>
                    <SelectShiftPeriodContent
                      type="clock-in"
                      groupBy={groupBy}
                      shiftEndTime={selectedPeriod?.endTime}
                      shiftPeriodId={selectedPeriod?.id}
                      shiftPlanId={shiftPlan?.id}
                      alreadyClockedIn={alreadyClockedIn}
                      alreadyClockedOut={alreadyClockedOut ?? ""}
                      latitude={employeeLatitude}
                      dutyLocationId={isWorkplace ? undefined : dutyLocationId}
                      longitude={employeeLongitude}
                      geofence={geofenceDisplay}
                      geofenceError={geofenceError}
                      isCheckingGeofence={
                        geofenceEnabled && geolocationStatus === "checking"
                      }
                      mutateClockIn={mutateClockIn}
                      isLoadingClockIn={isLoadingClockIn}
                      geofenceEnabled={geofenceEnabled}
                      status={clockInStatus?.status ?? ""}
                      currentTime={currentTime}
                      duration={clockInStatus?.duration ?? ""}
                      isOvertimeAllowed={isOvertimeAllowed}
                    />
                  </>
                ),
              },
              {
                key: "2",
                label: "I'm clocking out",
                children: (
                  <>
                    <p className="text-sm text-slate-900 font-semibold mb-2">
                      Select shift period you're clocking out for
                    </p>
                    <Radio.Group
                      style={{ width: "100%" }}
                      value={selectedPeriodId}
                      onChange={(e) => setSelectedPeriodId(e.target.value)}
                    >
                      <div className="grid [grid-template-columns:repeat(auto-fit,minmax(250px,1fr))] justify-between items-center gap-1">
                        {isLoading ? (
                          <Skeleton.Button
                            active
                            block
                            style={{ height: 60, borderRadius: 6 }}
                          />
                        ) : (
                          shiftPeriods?.map((period) => (
                            <ClockingCard
                              key={period.id}
                              value={period.id}
                              title={period.title}
                              clockIn={period.startTime}
                              clockOut={period.endTime}
                              onClick={() => setSelectedPeriodId(period.id)}
                            />
                          ))
                        )}
                      </div>
                    </Radio.Group>
                    <SelectShiftPeriodContent
                      type="clock-out"
                      groupBy={groupBy}
                      shiftPeriodId={selectedPeriod?.id}
                      shiftEndTime={selectedPeriod?.endTime}
                      shiftPlanId={shiftPlan?.id}
                      latitude={employeeLatitude}
                      alreadyClockedIn={alreadyClockedIn}
                      alreadyClockedOut={alreadyClockedOut ?? ""}
                      longitude={employeeLongitude}
                      geofence={geofenceDisplay}
                      geofenceError={geofenceError}
                      isCheckingGeofence={
                        geofenceEnabled && geolocationStatus === "checking"
                      }
                      mutateClockout={(body) => {
                        mutateClockout(body)
                      }}
                      isLoadingClockout={isLoadingClockout}
                      geofenceEnabled={geofenceEnabled}
                      status={clockOutStatus?.status ?? ""}
                      currentTime={currentTime}
                      duration={clockOutStatus?.duration ?? ""}
                      isOvertimeAllowed={isOvertimeAllowed}
                    />
                  </>
                ),
              },
            ]}
            onChange={(active) => setActiveTab(active)}
            className="[&_.ant-tabs-ink-bar-animated]:!hidden [&_.ant-tabs-nav]:bg-slate-100 [&_.ant-tabs-tab-btn]:text-black [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-black [&_.ant-tabs-nav]:p-1 [&_.ant-tabs-nav]:rounded-full [&_.ant-tabs-nav]:w-fit [&_.ant-tabs-tab]:px-4 [&_.ant-tabs-tab]:md:px-5 [&_.ant-tabs-tab]:py-2 [&_.ant-tabs-tab]:rounded-full [&_.ant-tabs-tab]:transition [&_.ant-tabs-tab-active]:bg-white [&_.ant-tabs-tab-active]:font-semibold"
          />
        </div>
      </div>
    </div>
  )
}

export default EmployeeClockInOut
